import React, { useEffect, useState } from 'react'
import { useParams } from 'react-router-dom'
import { useTranslation } from 'react-i18next'
import axios from 'axios'
import './EventDetail.css'
import { API_URL } from '../../api/base'
import { getLocalized } from '../../utils/localized'
import { formatDate as formatIntlDate, formatNumber as formatIntlNumber } from '../../utils/date'

interface EventLocation {
  id: number
  name: { ru?: string; ky?: string; en?: string }
  latitude: number | null
  longitude: number | null
  regionId: number | null
}

interface EventData {
  event: {
    id: string
    slug: string
    name: { ru: string; ky: string }
    description: { ru: string; ky: string }
    locationName: string
    locations?: EventLocation[]
    eventDate: string
    collectFrom: string
    collectTo: string
    targetTrees: number
    targetAmount: number
    status: string
    coverImage: string | null
  }
  stats: {
    totalTrees: number
    totalAmount: number
    totalDonors: number
  }
  companies: Array<{
    displayAmount: number
    comment: { ru: string; ky: string }
    company: {
      name: { ru: string; ky: string }
      logoUrl: string
      website: string
      tier: string
    }
  }>
  media: Array<{
    type: 'image' | 'video'
    url: string
    thumbnail: string | null
    caption: { ru: string; ky: string } | null
  }>
}

export const EventDetail: React.FC = () => {
  const { slug } = useParams<{ slug: string }>()
  const { t, i18n } = useTranslation()
  const [data, setData] = useState<EventData | null>(null)
  const [loading, setLoading] = useState(true)
  const [error, setError] = useState('')

  useEffect(() => {
    if (slug) fetchEvent()
  }, [slug])

  const fetchEvent = async () => {
    try {
      const { data } = await axios.get(`${API_URL}/api/events/${slug}`)
      setData(data)
    } catch (err) {
      setError(t('event_detail.load_error'))
      console.error(err)
    } finally {
      setLoading(false)
    }
  }

  const getLang = (text: { ru: string; ky: string } | null) =>
    getLocalized(text, i18n.language)

  const formatDate = (dateStr: string) =>
    formatIntlDate(dateStr.replace('Z', ''), i18n.language, {
      year: 'numeric',
      month: 'long',
      day: 'numeric'
    })

  const formatNumber = (num: number) => formatIntlNumber(num, i18n.language)

  const getProgress = () => {
    if (!data) return 0
    const { targetTrees, targetAmount } = data.event
    const { totalTrees, totalAmount } = data.stats

    if (targetTrees && totalTrees) {
      return Math.min((totalTrees / targetTrees) * 100, 100)
    }
    if (targetAmount && totalAmount) {
      return Math.min((totalAmount / targetAmount) * 100, 100)
    }
    return 0
  }

  if (loading) return <div className="event-detail-loading">{t('loading')}</div>
  if (error) return <div className="event-detail-error">{error}</div>
  if (!data) return null

  const { event, stats, companies, media } = data
  const progress = getProgress()

  return (
    <div className="event-detail">
      {event.coverImage && (
        <div className="event-hero">
          <img src={`${API_URL}${event.coverImage}`} alt={getLang(event.name)} />
          <div className="event-hero-overlay">
            <h1>{getLang(event.name)}</h1>
          </div>
        </div>
      )}

      <div className="event-detail-container">
        <div className="event-main">
          <div className="event-info">
            <h2>{getLang(event.name)}</h2>
            <p className="event-description" style={{ whiteSpace: 'pre-line' }}>{getLang(event.description)}</p>

            <div className="event-details">
              {event.locations && event.locations.length > 0 ? (
                event.locations.map((loc) => {
                  const label = getLocalized(loc.name, i18n.language) || ''
                  if (!label) return null
                  return (
                    <div className="detail-item" key={loc.id}>
                      <span className="detail-icon">📍</span>
                      <span>{label}</span>
                    </div>
                  )
                })
              ) : event.locationName ? (
                <div className="detail-item">
                  <span className="detail-icon">📍</span>
                  <span>{event.locationName}</span>
                </div>
              ) : null}
              <div className="detail-item">
                <span className="detail-icon">📅</span>
                <span>{formatDate(event.eventDate)}</span>
              </div>
            </div>
          </div>

          <div className="event-stats">
            <h3>{t('event_detail.statistics')}</h3>

            <div className="stats-grid">
              <div className="stat-card">
                <div className="stat-value">🌳 {formatNumber(stats.totalTrees)}</div>
                <div className="stat-label">
                  {t('event_detail.trees_planted')}
                </div>
              </div>

              <div className="stat-card">
                <div className="stat-value">💰 {formatNumber(stats.totalAmount)} {t('donations.currency')}</div>
                <div className="stat-label">
                  {t('event_detail.raised')}
                </div>
              </div>

              <div className="stat-card">
                <div className="stat-value">👥 {formatNumber(stats.totalDonors)}</div>
                <div className="stat-label">
                  {t('event_detail.donors')}
                </div>
              </div>
            </div>

            {event.targetTrees && (
              <div className="progress-section">
                <div className="progress-header">
                  <span>{t('event_detail.progress')}</span>
                  <span>{progress.toFixed(1)}%</span>
                </div>
                <div className="progress-bar">
                  <div
                    className="progress-fill"
                    // dynamic: live progress percentage
                    style={{ width: `${progress}%` }}
                  ></div>
                </div>
                <div className="progress-target">
                  {t('events_home.target', {
                    count: event.targetTrees,
                    amount: formatNumber(event.targetTrees)
                  })}
                </div>
              </div>
            )}

            <a href="/donate" className="donate-button">
              {t('event_detail.support')} →
            </a>
          </div>

          {companies.length > 0 && (
            <div className="event-partners">
              <h3>{t('event_detail.our_partners')}</h3>
              <div className="partners-grid">
                {companies.map((c, i) => (
                  <div key={i} className="partner-card">
                    {c.company.logoUrl && (
                      <img src={`${API_URL}${c.company.logoUrl}`} alt={getLang(c.company.name)} />
                    )}
                    <div className="partner-info">
                      <h4>{getLang(c.company.name)}</h4>
                      {c.displayAmount > 0 && (
                        <p className="partner-contribution">{formatNumber(c.displayAmount)} {t('donations.currency')}</p>
                      )}
                      {c.comment && <p className="partner-role">{getLang(c.comment)}</p>}
                    </div>
                  </div>
                ))}
              </div>
            </div>
          )}

          {media.length > 0 && (
            <div className="event-gallery">
              <h3>{t('event_detail.gallery')}</h3>
              <div className="gallery-grid">
                {media.map((m, i) => (
                  <div key={i} className="gallery-item">
                    {m.type === 'image' ? (
                      <img src={`${API_URL}${m.url}`} alt={m.caption ? getLang(m.caption) : ''} />
                    ) : (
                      <div className="video-placeholder">
                        {m.thumbnail && <img src={m.thumbnail} alt="Video" />}
                        <span className="play-icon">▶</span>
                      </div>
                    )}
                    {m.caption && <p className="gallery-caption">{getLang(m.caption)}</p>}
                  </div>
                ))}
              </div>
            </div>
          )}
        </div>
      </div>
    </div>
  )
}
